import React from 'react';

import {isAfter} from 'date-fns/isAfter';
import {isBefore} from 'date-fns/isBefore';
import {isSameDay} from 'date-fns/isSameDay';

import {useCalendarContext} from '../CalendarContext';
import {ECalendarType, TMonthDay} from '../types';
import {getDayHoveredStatus} from '../utils';
import * as S from './style';

type TProps = {
  onSelect: (date: Date) => void;
  day?: TMonthDay | null;
};

export const Day = React.memo<TProps>(({day, onSelect}) => {
  const {
    startDate,
    endDate,
    minDate,
    maxDate,
    hoveredDate,
    setHoveredDate,
    leadDate,
    now,
    calendarType,
  } = useCalendarContext();

  const selfDate = React.useMemo(
    () => (day ? new Date(day.year, day.month, day.day) : null),
    [day],
  );

  const isNow = React.useMemo(() => {
    if (!selfDate) {
      return false;
    }

    return isSameDay(selfDate, now);
  }, [selfDate, now]);

  const hoveredStatus = React.useMemo(
    () =>
      calendarType === ECalendarType.DATE_RANGE
        ? getDayHoveredStatus({
            currentDate: selfDate,
            startDate,
            endDate,
            hoveredDate,
            leadDate,
          })
        : null,
    [selfDate, startDate, endDate, hoveredDate, calendarType, leadDate],
  );

  const isSelected = React.useMemo(() => {
    if (!selfDate) {
      return;
    }

    return (
      (!!startDate && isSameDay(selfDate, startDate)) || (!!endDate && isSameDay(selfDate, endDate))
    );
  }, [selfDate, startDate, endDate]);

  const disabled = React.useMemo(() => {
    if (!selfDate) {
      return false;
    }

    return (!!maxDate && isAfter(selfDate, maxDate)) || (!!minDate && isBefore(selfDate, minDate));
  }, [selfDate, minDate, maxDate]);

  const handleMouseEnter = React.useCallback(
    () => setHoveredDate(selfDate),
    [setHoveredDate, selfDate],
  );

  const handleMouseLeave = React.useCallback(() => setHoveredDate(null), [setHoveredDate]);

  const handleSelect = React.useCallback(() => {
    if (selfDate) {
      onSelect(selfDate);
    }
  }, [selfDate, onSelect]);

  if (!day?.day) {
    return <S.EmptyDate isRound />;
  }

  return (
    <S.Date
      isRound
      disabled={disabled}
      onClick={handleSelect}
      isHovered={hoveredStatus?.isHovered}
      isEndHovered={hoveredStatus?.isEndHovered}
      isStartHovered={hoveredStatus?.isStartHovered}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-selector={`EdnaCalendar:day-${day.day}`}
    >
      <S.DateStatus isRound isNow={isNow} isSelected={isSelected} disabled={disabled}>
        {day.day}
      </S.DateStatus>
    </S.Date>
  );
});
